import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, ActivityIndicator, Image } from 'react-native';
import { useNavigation, useRoute, RouteProp, CompositeNavigationProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import QuoteCard from '../../components/work/QuoteCard';
import EmptyState from '../../components/work/EmptyState';
import EndContractModal from '../../components/work/EndContractModal';
import { WorkStackParamList, RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useWork } from '../../context/WorkContext';
import { useAuth } from '../../context/AuthContext';
import ThemedText from '../../components/common/ThemedText';
import { Job, Quote } from '../../types/work';
import { showSuccessToast, showErrorToast, Alert } from '../../utils/toast';
import { buildApiUrl } from '../../config/env';
import { storage } from '../../utils/storage';
import { shareItem } from '../../utils/share';

type SupplyDetailNavigationProp = CompositeNavigationProp<
  StackNavigationProp<WorkStackParamList, 'SupplyDetail'>,
  StackNavigationProp<RootStackParamList>
>;
type SupplyDetailRouteProp = RouteProp<WorkStackParamList, 'SupplyDetail'>;

const SupplyDetailScreen: React.FC = () => {
  const navigation = useNavigation<SupplyDetailNavigationProp>();
  const route = useRoute<SupplyDetailRouteProp>();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { fetchJobById, fetchJobQuotes, fetchMyQuotes, myQuotes, acceptQuote, requestCompletion, confirmCompletion } = useWork();
  const { jobId } = route.params;
  
  const [job, setJob] = useState<Job | null>(null);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [myQuote, setMyQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [activeTab, setActiveTab] = useState<'quotes' | 'details'>('quotes');
  const [pendingCompletionRequests, setPendingCompletionRequests] = useState<Record<string, any>>({});
  const [endContractModalVisible, setEndContractModalVisible] = useState(false);
  const [selectedQuoteForEndContract, setSelectedQuoteForEndContract] = useState<Quote | null>(null);
  const [endContractMode, setEndContractMode] = useState<'request' | 'respond'>('request');

  useEffect(() => {
    loadJob();
  }, [jobId]);

  useEffect(() => {
    const existingQuote = myQuotes.find(q => q.jobId === jobId);
    setMyQuote(existingQuote || null);
  }, [myQuotes, jobId]);

  useEffect(() => {
    // Fetch completion requests for all accepted quotes
    const acceptedQuotes = [...quotes, ...(myQuote ? [myQuote] : [])].filter(q => q.status === 'accepted');
    acceptedQuotes.forEach(quote => {
      fetchCompletionRequest(quote.id);
    });
  }, [quotes, myQuote]);

  const loadJob = async () => {
    setLoading(true);
    const jobData = await fetchJobById(jobId);
    setJob(jobData);
    
    if (jobData && jobData.createdBy.id === user?.userId) {
      setLoadingQuotes(true);
      const quotesData = await fetchJobQuotes(jobId);
      setQuotes(quotesData);
      setLoadingQuotes(false);
    } else {
      await fetchMyQuotes();
    }
    
    setLoading(false);
  };

  const fetchCompletionRequest = async (quoteId: string) => {
    try {
      const token = await storage.getToken();
      const response = await fetch(buildApiUrl(`api/quotes/${quoteId}/completion-request`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success && data.completionRequest) {
        setPendingCompletionRequests(prev => ({
          ...prev,
          [quoteId]: data.completionRequest,
        }));
      }
    } catch (error) {
      console.error('Error fetching completion request:', error);
    }
  };

  const handleEndContractRequest = async (requestedStatus: 'completed' | 'partially_completed' | 'cancelled') => {
    if (!selectedQuoteForEndContract) return;
    
    try {
      const token = await storage.getToken();
      const response = await fetch(buildApiUrl(`api/quotes/${selectedQuoteForEndContract.id}/request-end-contract`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ requestedStatus }),
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccessToast('End contract request sent successfully!');
        // Refresh completion request
        await fetchCompletionRequest(selectedQuoteForEndContract.id);
        // Reload quotes
        if (isMyJob) {
          const quotesData = await fetchJobQuotes(jobId);
          setQuotes(quotesData);
        } else {
          await fetchMyQuotes();
        }
      } else {
        throw new Error(data.message || 'Failed to send request');
      }
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to send end contract request');
      throw error;
    }
  };

  const handleRespondToEndContract = async (action: 'accept' | 'reject') => {
    if (!selectedQuoteForEndContract) return;
    
    try {
      const token = await storage.getToken();
      const response = await fetch(buildApiUrl(`api/quotes/${selectedQuoteForEndContract.id}/respond-end-contract`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action }),
      });
      
      const data = await response.json();
      if (data.success) {
        if (action === 'accept') {
          showSuccessToast('Contract ended successfully!');
        } else {
          showSuccessToast('Request rejected. The other party can submit a new request.');
        }
        
        // Refresh completion request
        await fetchCompletionRequest(selectedQuoteForEndContract.id);
        // Reload quotes
        if (isMyJob) {
          const quotesData = await fetchJobQuotes(jobId);
          setQuotes(quotesData);
        } else {
          await fetchMyQuotes();
        }
      } else {
        throw new Error(data.message || 'Failed to respond');
      }
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to respond to request');
      throw error;
    }
  };

  const isMyJob = job?.createdBy.id === user?.userId;
  const isAdmin = user?.role === 'admin';
  const canCloseRequest = isMyJob || isAdmin;
  const isJobOpen = job?.status === 'open';

  useEffect(() => {
    setActiveTab(isMyJob ? 'quotes' : 'details');
  }, [isMyJob]);

  const handleEditJob = () => {
    navigation.navigate('EditJob', { jobId });
  };

  const handleShare = async () => {
    if (!job) return;
    
    await shareItem({
      id: jobId,
      title: job.title,
      type: 'supply',
      description: job.description,
      budget: job.budget,
    });
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </SafeAreaView>
    );
  }

  if (!job) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <EmptyState icon="alert-circle-outline" message="Supply request not found" />
      </SafeAreaView>
    );
  }

  const handleSendQuote = () => {
    navigation.navigate('SendQuote', { jobId, job });
  };

  const handleViewProfile = (userId: string) => {
    navigation.navigate('Profile', { userId });
  };

  const handleStartChat = (userId: string, userName: string) => {
    navigation.navigate('PersonalChat', { userId, userName });
  };

  const handleAcceptQuote = async (quoteId: string, freelancerName: string) => {
    Alert.alert(
      'Accept Quote',
      `Are you sure you want to accept the quote from ${freelancerName}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Accept',
          onPress: async () => {
            try {
              await acceptQuote(quoteId);
              // Reload quotes to show updated status
              const quotesData = await fetchJobQuotes(jobId);
              setQuotes(quotesData);
              showSuccessToast('Quote accepted successfully!');
            } catch (error) {
              showErrorToast('Failed to accept quote. Please try again.');
            }
          },
        },
      ]
    );
  };

  const handleEditQuote = (quote: Quote) => {
    navigation.navigate('EditQuote', { quoteId: quote.id, quote });
  };

  const handleRequestCompletion = async (quoteId: string) => {
    Alert.alert(
      'Request Completion',
      'Are you sure you want to request completion? The other party will need to confirm before the contract is marked as completed.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Request',
          onPress: async () => {
            try {
              await requestCompletion(quoteId);
              await fetchMyQuotes();
              showSuccessToast('Completion requested! Waiting for confirmation from the other party.');
            } catch (error: any) {
              showErrorToast(error.message || 'Failed to request completion.');
            }
          },
        },
      ]
    );
  };

  const handleConfirmCompletion = async (quoteId: string) => {
    Alert.alert(
      'Confirm Completion',
      'Are you sure you want to confirm completion? This will mark the contract as completed.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          onPress: async () => {
            try {
              await confirmCompletion(quoteId);
              await fetchMyQuotes();
              showSuccessToast('Work completed successfully!');
            } catch (error: any) {
              showErrorToast(error.message || 'Failed to confirm completion.');
            }
          },
        },
      ]
    );
  };

  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return `${Math.floor(diffInDays / 30)} months ago`;
  };

  const renderDetailsView = () => {
    return (
      <View style={styles.detailsContainer}>
        {/* Supply Details Section */}
        <View style={[styles.infoSection, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <View style={styles.infoSectionHeader}>
            <Ionicons name="cube-outline" size={20} color={theme.accent} />
            <ThemedText style={[styles.infoSectionTitle, { color: theme.text }]}>
              Supply Requirements
            </ThemedText>
          </View>
          
          <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
            <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Quantity</ThemedText>
            <ThemedText style={[styles.infoValue, { color: theme.text }]}>
              {(job as any).quantity} {(job as any).unit}
            </ThemedText>
          </View>

          <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
            <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Category</ThemedText>
            <ThemedText style={[styles.infoValue, { color: theme.text }]}>{job.category}</ThemedText>
          </View>

          {job.budget && (
            <View style={[styles.infoRow, { borderBottomColor: 'transparent' }]}>
              <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Budget</ThemedText>
              <ThemedText style={[styles.infoValue, { color: theme.text }]}>
                ${job.budget.toLocaleString()}
              </ThemedText>
            </View>
          )}
        </View>

        {/* Location & Requirements */}
        <View style={[styles.infoSection, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <View style={styles.infoSectionHeader}>
            <Ionicons name="location-outline" size={20} color={theme.accent} />
            <ThemedText style={[styles.infoSectionTitle, { color: theme.text }]}>
              Location & Details
            </ThemedText>
          </View>
          
          {(job as any).state && (
            <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
              <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>State</ThemedText>
              <ThemedText style={[styles.infoValue, { color: theme.text }]}>
                {(job as any).state}
              </ThemedText>
            </View>
          )}

          {(job as any).district && (
            <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
              <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>District</ThemedText>
              <ThemedText style={[styles.infoValue, { color: theme.text }]}>
                {(job as any).district}
              </ThemedText>
            </View>
          )}
          
          <View style={[styles.infoRow, { borderBottomColor: 'transparent' }]}>
            <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Posted</ThemedText>
            <ThemedText style={[styles.infoValue, { color: theme.text }]}>
              {getRelativeTime(new Date(job.createdAt))}
            </ThemedText>
          </View>
        </View>

        {/* Owner Actions */}
        {canCloseRequest && isJobOpen && (
          <View style={styles.ownerActionsSection}>
            <TouchableOpacity
              style={[styles.closeJobButton, { backgroundColor: theme.error }]}
              onPress={() => {
                Alert.alert(
                  'Close Supply Request',
                  'Are you sure you want to close this supply request? You can still view it but won\'t receive new quotations.',
                  [
                    { text: 'Cancel', style: 'cancel' },
                    {
                      text: 'Close Request',
                      style: 'destructive',
                      onPress: async () => {
                        try {
                          // Update job status to completed or closed
                          // You'll need to add this function to WorkContext
                          Alert.alert('Success', 'Supply request closed successfully');
                          navigation.goBack();
                        } catch (error) {
                          Alert.alert('Error', 'Failed to close supply request');
                        }
                      },
                    },
                  ]
                );
              }}
            >
              <Ionicons name="close-circle" size={20} color="#FFFFFF" />
              <ThemedText style={styles.closeJobButtonText}>Close This Request</ThemedText>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  const renderFreelancerView = () => {
    if (myQuote) {
      const pendingRequest = pendingCompletionRequests[myQuote.id];
      const hasPendingRequest = !!pendingRequest;
      const isRequester = pendingRequest?.requestedById === user?.userId;
      
      return (
        <View style={styles.quotesSection}>
          <QuoteCard
            quote={myQuote}
            jobTitle={job!.title}
            currentUserId={user?.userId}
            onPress={() => {}}
            showFreelancerInfo={false}
            showChatButton={true}
            onChatPress={() => handleStartChat(job!.createdBy.id, job!.createdBy.name || 'Client')}
            showEditButton={myQuote.status === 'sent'}
            onEditPress={() => handleEditQuote(myQuote)}
            showEndContractButton={myQuote.status === 'accepted' && !hasPendingRequest}
            onEndContractPress={() => {
              setSelectedQuoteForEndContract(myQuote);
              setEndContractMode('request');
              setEndContractModalVisible(true);
            }}
            showRespondEndContractButton={hasPendingRequest && !isRequester}
            onRespondEndContractPress={() => {
              setSelectedQuoteForEndContract(myQuote);
              setEndContractMode('respond');
              setEndContractModalVisible(true);
            }}
            pendingCompletionRequest={pendingRequest}
          />
        </View>
      );
    }

    return (
      <View style={styles.actionSection}>
        <TouchableOpacity
          style={[styles.quoteButton, { backgroundColor: theme.accent }]}
          onPress={handleSendQuote}
        >
          <ThemedText style={[styles.quoteButtonText, { color: theme.textInverse }]}>
            Send Quotation
          </ThemedText>
        </TouchableOpacity>
      </View>
    );
  };

  const renderClientView = () => {
    return (
      <View style={styles.quotesSection}>
        {loadingQuotes ? (
          <View style={styles.quotesLoading}>
            <ActivityIndicator size="small" color={theme.accent} />
          </View>
        ) : quotes.length > 0 ? (
          quotes.map((quote) => {
            const pendingRequest = pendingCompletionRequests[quote.id];
            const hasPendingRequest = !!pendingRequest;
            const isRequester = pendingRequest?.requestedById === user?.userId;
            
            return (
              <View key={quote.id} style={styles.quoteCardWrapper}>
                <QuoteCard 
                  quote={quote}
                  jobTitle={job.title}
                  currentUserId={user?.userId}
                  showFreelancerInfo={true}
                  onProfilePress={() => handleViewProfile(quote.freelancer?.id || '')}
                  showChatButton={true}
                  onChatPress={() => handleStartChat(quote.freelancer?.id || '', quote.freelancer?.name || 'Supplier')}
                  onPress={() => {}}
                  showAcceptButton={quote.status === 'sent'}
                  onAcceptPress={() => handleAcceptQuote(quote.id, quote.freelancer?.name || 'Supplier')}
                  showEndContractButton={quote.status === 'accepted' && !hasPendingRequest}
                  onEndContractPress={() => {
                    setSelectedQuoteForEndContract(quote);
                    setEndContractMode('request');
                    setEndContractModalVisible(true);
                  }}
                  showRespondEndContractButton={hasPendingRequest && !isRequester}
                  onRespondEndContractPress={() => {
                    setSelectedQuoteForEndContract(quote);
                    setEndContractMode('respond');
                    setEndContractModalVisible(true);
                  }}
                  pendingCompletionRequest={pendingRequest}
                />
              </View>
            );
          })
        ) : (
          <EmptyState 
            icon="document-text-outline" 
            message="No quotations received yet"
          />
        )}
      </View>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return '#10B981';
      case 'in_progress': return '#F59E0B';
      case 'completed': return '#3B82F6';
      case 'cancelled': return '#EF4444';
      default: return theme.accent;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Open';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerButton}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Supply Details</ThemedText>
        <View style={styles.headerActions}>
          {isMyJob && (
            <TouchableOpacity style={styles.headerActionButton} onPress={handleEditJob}>
              <Ionicons name="create-outline" size={24} color={theme.text} />
            </TouchableOpacity>
          )}
          <TouchableOpacity style={styles.headerActionButton} onPress={handleShare}>
            <Ionicons name="share-outline" size={24} color={theme.text} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Content */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Job Info Section */}
        <View style={[styles.jobInfo, { backgroundColor: theme.background }]}>
          <View style={styles.titleRow}>
            <ThemedText style={[styles.jobTitle, { color: theme.text }]}>{job.title}</ThemedText>
          </View>
          
          <View style={styles.metaRow}>
            <TouchableOpacity 
              style={styles.clientInfo}
              onPress={() => handleViewProfile(job.createdBy.id)}
              activeOpacity={0.7}
            >
              {job.createdBy.photo ? (
                <Image source={{ uri: job.createdBy.photo }} style={styles.clientAvatar} />
              ) : (
                <View style={[styles.clientAvatar, { backgroundColor: theme.accent }]}>
                  <ThemedText style={styles.clientAvatarText}>
                    {job.createdBy.name?.charAt(0).toUpperCase() || '?'}
                  </ThemedText>
                </View>
              )}
              <View style={styles.clientText}>
                <ThemedText style={[styles.clientLabel, { color: theme.textSecondary }]}>
                  Requester
                </ThemedText>
                <ThemedText style={[styles.clientName, { color: theme.text }]}>
                  {job.createdBy.name || 'Unknown'}
                </ThemedText>
              </View>
              <Ionicons name="chevron-forward" size={20} color={theme.textSecondary} style={{ marginLeft: 8 }} />
            </TouchableOpacity>
            <ThemedText style={[styles.time, { color: theme.textSecondary }]}>
              {getRelativeTime(new Date(job.createdAt))}
            </ThemedText>
          </View>

          {/* Detail Card */}
          <View style={[styles.detailCard, { borderColor: theme.border }]}>
            <View style={styles.detailItem}>
              <ThemedText style={[styles.detailLabel, { color: theme.textSecondary }]}>
                Budget
              </ThemedText>
              <ThemedText style={[styles.detailValue, { color: theme.text }]}>
                {job.budget ? `$${job.budget.toLocaleString()}` : 'N/A'}
              </ThemedText>
            </View>
            
            <View style={[styles.detailDivider, { backgroundColor: theme.border }]} />
            
            <View style={styles.detailItem}>
              <ThemedText style={[styles.detailLabel, { color: theme.textSecondary }]}>
                Category
              </ThemedText>
              <ThemedText 
                style={[styles.detailValue, { color: theme.text }]}
                numberOfLines={1}
                adjustsFontSizeToFit
              >
                {job.category}
              </ThemedText>
            </View>
            
            <View style={[styles.detailDivider, { backgroundColor: theme.border }]} />
            
            <View style={styles.detailItem}>
              <ThemedText style={[styles.detailLabel, { color: theme.textSecondary }]}>
                Status
              </ThemedText>
              <View style={[styles.statusPill, { backgroundColor: `${getStatusColor(job.status)}20` }]}>
                <ThemedText style={[styles.statusPillText, { color: getStatusColor(job.status) }]}>
                  {getStatusLabel(job.status)}
                </ThemedText>
              </View>
            </View>
          </View>

          {/* Description */}
          <View style={styles.descriptionSection}>
            <ThemedText style={[styles.descriptionTitle, { color: theme.text }]}>
              Description
            </ThemedText>
            <ThemedText style={[styles.descriptionText, { color: theme.textSecondary }]}>
              {job.description}
            </ThemedText>
          </View>
        </View>

        {/* Tab Switcher */}
        <View style={[styles.tabSwitcher, { borderBottomColor: theme.border }]}>
          <TouchableOpacity
            style={styles.tabButton}
            onPress={() => setActiveTab('quotes')}
          >
            <ThemedText
              style={[
                styles.tabButtonText,
                { color: activeTab === 'quotes' ? theme.accent : theme.textSecondary }
              ]}
            >
              {isMyJob ? `Quotations (${quotes.length})` : 'Quotations'}
            </ThemedText>
            {activeTab === 'quotes' && (
              <View style={[styles.activeIndicator, { backgroundColor: theme.accent }]} />
            )}
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.tabButton}
            onPress={() => setActiveTab('details')}
          >
            <ThemedText
              style={[
                styles.tabButtonText,
                { color: activeTab === 'details' ? theme.accent : theme.textSecondary }
              ]}
            >
              Details
            </ThemedText>
            {activeTab === 'details' && (
              <View style={[styles.activeIndicator, { backgroundColor: theme.accent }]} />
            )}
          </TouchableOpacity>
        </View>

        {/* Tab Content */}
        {activeTab === 'quotes' && (isMyJob ? renderClientView() : renderFreelancerView())}
        {activeTab === 'details' && renderDetailsView()}
      </ScrollView>

      {/* End Contract Modal */}
      <EndContractModal
        visible={endContractModalVisible}
        onClose={() => {
          setEndContractModalVisible(false);
          setSelectedQuoteForEndContract(null);
        }}
        onSubmit={async (statusOrAction) => {
          if (endContractMode === 'request') {
            await handleEndContractRequest(statusOrAction as 'completed' | 'partially_completed' | 'cancelled');
          } else {
            await handleRespondToEndContract(statusOrAction as 'accept' | 'reject');
          }
        }}
        mode={endContractMode}
        requestedStatus={
          endContractMode === 'respond' && selectedQuoteForEndContract
            ? pendingCompletionRequests[selectedQuoteForEndContract.id]?.requestedStatus
            : undefined
        }
        jobTitle={job?.title || ''}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    gap: 4,
  },
  headerActionButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  jobInfo: {
    padding: 16,
    marginBottom: 12,
  },
  titleRow: {
    marginBottom: 16,
  },
  jobTitle: {
    fontSize: 20,
    fontWeight: '700',
    flex: 1,
    marginRight: 12,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  clientInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  clientAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  clientAvatarText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  clientText: {
    marginLeft: 12,
  },
  clientLabel: {
    fontSize: 12,
  },
  clientName: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 2,
  },
  time: {
    fontSize: 12,
  },
  detailCard: {
    flexDirection: 'row',
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: 16,
    alignItems: 'stretch',
  },
  detailItem: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    textAlign: 'center',
  },
  detailValue: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  detailDivider: {
    width: 1,
    alignSelf: 'stretch',
    opacity: 0.4,
  },
  statusPill: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    alignSelf: 'center',
  },
  statusPillText: {
    fontSize: 13,
    fontWeight: '600',
  },
  descriptionSection: {
    marginTop: 8,
  },
  descriptionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  descriptionText: {
    fontSize: 15,
    lineHeight: 22,
  },
  detailsContainer: {
    padding: 16,
  },
  infoSection: {
    borderWidth: 1,
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
  },
  infoSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  infoSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  infoLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  infoValue: {
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'right',
    flexShrink: 1,
    marginLeft: 16,
  },
  actionSection: {
    padding: 16,
  },
  quoteButton: {
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  quoteButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  quotesSection: {
    paddingVertical: 16,
  },
  quotesLoading: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
  },
  quoteCardWrapper: {
    marginBottom: 16,
  },
  quoteSentCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  quoteSentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  quoteSentTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  quoteSentDetails: {
    gap: 12,
  },
  quoteSentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  quoteSentLabel: {
    fontSize: 14,
  },
  quoteSentAmount: {
    fontSize: 20,
    fontWeight: '700',
  },
  quoteSentStatus: {
    fontSize: 14,
    fontWeight: '600',
  },
  quoteSentMessageBox: {
    gap: 4,
  },
  quoteSentMessageLabel: {
    fontSize: 12,
  },
  quoteSentMessage: {
    fontSize: 14,
    lineHeight: 20,
  },
  tabSwitcher: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    marginTop: 0,
    marginBottom: 0,
    borderBottomWidth: 1,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 14,
    alignItems: 'center',
    position: 'relative',
  },
  activeIndicator: {
    position: 'absolute',
    bottom: -1,
    left: '10%',
    right: '10%',
    height: 3,
    borderTopLeftRadius: 999,
    borderTopRightRadius: 999,
  },
  tabButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  ownerActionsSection: {
    padding: 16,
    paddingTop: 8,
  },
  closeJobButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 8,
    gap: 8,
  },
  closeJobButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default SupplyDetailScreen;
